import { create, StateCreator } from 'zustand';
import { persist, PersistOptions } from 'zustand/middleware';

// Define the type for the filter options
type FilterOptions = {
  account_ids: string[] | null;
  ticket_status_ids: string[] | null;
  ticket_type_ids: string[] | null;
  function_ids: string[] | null;
  department_ids: string[] | null;
  country_ids: string[] | null;
  internal_ticket_type_ids: string[] | null;
  internal_owner_ids: string[] | null;
  watched: boolean | null;
  created_by_me: boolean | null;
};

// Define the store type, focusing only on filters
type FilterStore = {
  filters: FilterOptions;
  setStorageFilter: (filterType: keyof FilterOptions, values: string[] | null | boolean) => void;
  setStorageFilters: (newFilters: Partial<FilterOptions>) => void;
  resetStorageFilters: () => void;
  getStorageFilter: <T extends keyof FilterOptions>(filterType: T) => FilterOptions[T];
};

// Define the persist options type for `FilterStore`
type FilterStorePersist = (
  config: StateCreator<FilterStore>,
  options: PersistOptions<FilterStore>
) => StateCreator<FilterStore>;

// Create the zustand store for filter options
const useTicketFilterStore = create<FilterStore>(
  (persist as FilterStorePersist)(
    (set, get) => ({
      filters: {
        account_ids: null,
        ticket_status_ids: null,
        ticket_type_ids: null,
        function_ids: null,
        department_ids: null,
        country_ids: null,
        internal_ticket_type_ids: null,
        internal_owner_ids: null,
        watched: null,
        created_by_me: null,
      }, // Default empty filters

      setStorageFilter: (filterType, values) =>
        set((state) => ({
          filters: {
            ...state.filters,
            [filterType]: Array.isArray(values) && values.length === 0 ? null : values,
          },
        })),

      setStorageFilters: (newFilters) =>
        set((state) => ({
          filters: {
            ...state.filters,
            ...newFilters, // Spread and merge the new filters
          },
        })),

      resetStorageFilters: () =>
        set(() => ({
          filters: {
            account_ids: null,
            ticket_status_ids: null,
            ticket_type_ids: null,
            function_ids: null,
            department_ids: null,
            country_ids: null,
            internal_ticket_type_ids: null,
            internal_owner_ids: null,
            watched: null,
            created_by_me: null,
          },
        })),

        getStorageFilter: (filterType) => get().filters[filterType],
    }),
    {
      name: 'ticket-index-filter-options', // Key for localStorage
    }
  )
);

export default useTicketFilterStore;
